import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';

import Input from 'antd/es/input';
import Select from 'antd/es/select';

import * as Yup from 'yup';

import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import loadable from '@common/react/loadable/loadableSettings';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { FormikPhoneControl } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import {
	emailValidator,
	notNullValidator,
	phoneRequiredValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { useMobileView } from '@common/react/hooks/useMobileView';

import { Gender, GenderNames } from '@commonTuna/react/objects/Enums';
import { Device } from '@commonTuna/react/objects/Device';

import { LocationPortal } from '@app/objects/CompanyPortal';
import { getLocationName } from '@app/utils/Utils';
import { Doctor } from '@app/objects/Doctor';
import DatePickerLoader from '@app/components/UI/DatePickerLoader';
import { User } from '@app/objects/User';

import '@app/scss/components/inquiryForm.scss';

const DatePickerReact = loadable(
	() => import(/* webpackChunkName: "CustomDatePickerReact" */ '@common/react/components/UI/DatePickerReact/AdaptiveDatepicker'),
	{ fallback: <DatePickerLoader placeholder="Date..." /> },
);

interface InquiryFormValues {
	id: number;
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
	text: string;
	gender: Gender | null;
	locationId: number | null;
	doctorId: number;
}

interface InquiryFormProps {
	doctor?: Doctor;
	doctorId?: number;
	locationId?: number;
	doctors?: Array<Doctor>;
	locations: Array<LocationPortal>;
	selectedLocationId?: number;
}

const getValidationSchema = (locationRequired: boolean) => Yup.object().shape({
	locationId: locationRequired ? notNullValidator : Yup.mixed().notRequired(),
	firstName: simpleStringValidator,
	lastName: simpleStringValidator,
	phone: phoneRequiredValidator,
	email: emailValidator,
	doctorId: notNullValidator,
});

const year = new Date().getFullYear();

const years = Array.from({ length: 83 }).map((_, i) => year - 100 + i);

const doctorsByLocationId = (doctors: Array<Doctor> | undefined, locationId?: null | number) => {
	return doctors?.filter((doctor) => !locationId || locationId < 0 || doctor.locations?.some((location) => location.locationId === locationId));
};

const InquiryForm: React.FC<InquiryFormProps> = ({
	doctor,
	doctors,
	locationId,
	doctorId,
	locations,
	selectedLocationId,
}) => {
	const { getUser } = useApplicationContext();
	const { openErrorMessage } = useModal();
	const user = getUser<User>();
	const solveDoctors = (!doctor || !locationId) && locations.length > 1;
	const initDoctorId = React.useMemo(() => {
		const initDoctors = doctorsByLocationId(doctors, locationId);
		return solveDoctors && !(doctorId && initDoctors?.some((item) => item.id === doctorId)) ? initDoctors?.[0].id : doctorId;
	}, []);

	const [success, setSuccess] = React.useState(false);
	const timer = React.useRef<any>(null);
	const [data, setData] = React.useState<InquiryFormValues>();

	const { minDate, maxDate } = React.useMemo(() => {
		const currentDate = new Date();
		return {
			minDate: new Date(currentDate.getFullYear() - 100, currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0),
			maxDate: new Date(currentDate.getFullYear() - 18, currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0),
		};
	}, []);

	const isMobile = useMobileView();
	const location = useLocation();

	const showDateOfBirth = locations?.[0]?.company?.showDateOfBirth;
	React.useEffect(() => {
		return () => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
	}, []);

	const locationRequired = !!locations?.length;

	const validationSchema = getValidationSchema(locationRequired);

	return (
		<div className="inquiry-form">
			<ItemProvider<InquiryFormValues>
				key={user?.id}
				id={-1}
				saveRequest="sendInquiry"
				onSaveRequestError={() => openErrorMessage?.('Something went wrong!')}
				clearForSubmit={({ item }) => ({
					...item,
					name: `${item.firstName} ${item.lastName}`,
				})}
				validationSchema={validationSchema}
			>
				<ItemEditor<InquiryFormValues>
					withButtons
					beforeSubmit={({ values, actions, submit }) => {
						setData(values as any);
						submit()
							.then(() => {
								actions.resetForm();
								setSuccess(true);
								timer.current = setTimeout(() => {
									setSuccess(false);
									timer.current = null;
								}, user ? 5000 : 30000);
							});
					}}
					successMessage=""
					formikProps={{
						enableReinitialize: true,
					}}
					saveText="Submit"
					getInitialValues={() => ({
						firstName: user?.firstName || '',
						lastName: user?.lastName || '',
						phone: user?.phoneNumber || '',
						email: user?.email || '',
						text: '',
						gender: user?.gender ?? Gender.Female,
						locationId: locationId || selectedLocationId || (locations?.length ? locations[0].id : null),
						doctorId: initDoctorId,
						device: isMobile ? Device.Mobile : Device.Desktop,
						path: location.pathname,
						birthDate: user?.birthDate,
					})}
					edit={({ formikBag }) => <>
						<div className="row">
							<FormikField
								title="First Name*"
								fieldName="firstName"
								inputProps={{
									maxLength: 50,
								}}
								containerClassName="form-group col-sm-12"
							/>
						</div>
						<div className="row">
							<FormikField
								title="Last Name*"
								fieldName="lastName"
								inputProps={{
									maxLength: 50,
								}}
								containerClassName="form-group col-sm-12"
							/>
						</div>
						<div className="row">
							<FormikField
								fieldName="phone"
								title="Phone Number*"
								containerClassName={`form-group col-sm-${showDateOfBirth ? '6' : '12'}`}
								render={(fieldProps) =>
									<FormikPhoneControl
										fieldProps={fieldProps}
										placeholder="+1 (___) ___-____"
									/>
								}
							/>
							<FormikField
								title="Email*"
								fieldName="email"
								containerClassName={`form-group col-sm-${showDateOfBirth ? '6' : '12'}`}
							/>
						</div>
						<div className="row">
							<FormikField
								title="Gender*"
								fieldName="gender"
								containerClassName={`form-group col-sm-${showDateOfBirth ? '6' : '12'}`}
								render={({ field, form }) => <>
									<Select
										placeholder=" "
										defaultValue={Gender.Female}
										onChange={(value) => {
											form.setFieldValue(field.name, +value);
										}}
										value={GenderNames[field.value]}
									>

										{Object.keys(GenderNames).filter((key) => +key !== Gender.None).map((key) =>
											<Select.Option value={key} key={key}>{GenderNames[key]}</Select.Option>)}
									</Select>
								</>
								}
							/>
							{showDateOfBirth
								&& <FormikField
									fieldName="birthDate"
									title="Birth Date"
									containerClassName="form-group col-sm-6"
									render={({ field, form }) => (
										<DatePickerReact
											value={field.value}
											yearsSelectOptions={years}
											placeholder="Select date"
											onChange={(date) => form.setFieldValue(field.name, date)}
											datePickerProps={{
												showMonthDropdown: true,
												showYearDropdown: true,
												dropdownMode: 'select',
												maxDate,
												minDate,
												popperClassName: 'custom-date-picker-container',
												popperContainer: typeof window !== 'undefined'
													? ({ children }) => createPortal(children, document.body) : undefined,
											}}
											utc
										/>
									)}
								/>}
						</div>
						{doctor ? <div className="row">
							<FormikField
								title="Provider"
								fieldName="doctorId"
								containerClassName="form-group col-sm-12"
								render={({ field }) =>
									<Select
										placeholder=" "
										value={field.value}
									>
										<Select.Option key={`location-${doctor.id}`} value={doctor.id}>
											{doctor?.nameEn}
										</Select.Option>
									</Select>
								}
							/>
						</div> : null}
						{locations ? <div className="row">
							<FormikField
								title={<>
									Location
									{locationRequired ? '*' : ''}
								</> as any}
								fieldName="locationId"
								containerClassName="form-group col-sm-12"
								render={({ field, form }) =>
									<Select
										placeholder="Location"
										onChange={(value) => {
											form.setFieldValue(field.name, value);
											const solvedDoctors = solveDoctors ? doctorsByLocationId(doctors, value) : doctors;
											if (!(solvedDoctors?.some((item) => item.id === formikBag.values.doctorId))) {
												form.setFieldValue('doctorId', solvedDoctors?.[0]?.id ?? null);
											}
										}}
										value={field.value}
									>
										{locations?.map((location) => <Select.Option key={`location-${location.id}`} value={location.id}>
											{getLocationName(location)}
										</Select.Option>)}
									</Select>
								}
							/>
						</div> : null}
						{doctors ? <div className="row">
							<FormikField
								title="Provider*"
								fieldName="doctorId"
								containerClassName="form-group col-sm-12"
								render={({ field, form }) =>
									<Select
										placeholder=" "
										onChange={(value) => form.setFieldValue(field.name, value)}
										value={field.value}
									>
										{(solveDoctors ? doctorsByLocationId(doctors, formikBag.values.locationId) : doctors)
											?.map((doctor) => <Select.Option key={`doctor-${doctor.id}`} value={doctor.id}>
												{doctor.nameEn}
											</Select.Option>)}
									</Select>
								}
							/>
						</div> : null}
						<div className="row">
							<FormikField
								title="Text"
								fieldName="text"
								containerClassName="form-group col-sm-12"
								render={({ field }) =>
									<Input.TextArea
										className="form-control"
										maxLength={500}
										style={{ height: 90, resize: 'none' }}
										{...field}
									/>
								}
							/>
						</div>
					</>}
				/>
			</ItemProvider>
			{success ? <div className="inquiry-form__success">
				<div>
					<i className="fa fa-check" />
					<h2>
						success
					</h2>
				</div>
				<div>
					{/* Your request has been sent successfully. */}
					{user ? <>
						<p>
							Thank you! Clinic front desk will contact you as soon as possible.
						</p>
						<Link
							className="btn btn-primary"
							to={{ pathname: '/doctors' }}
						>
							Find More Providers
						</Link>
					</>
						: <>
							<p>
								Complete quick registration to get even more features.
							</p>
							<Link
								className="btn btn-primary"
								state={data}
								to={{ pathname: '/registration' }}
							>
								Registration
							</Link>
							{/*
							{' '}
							<Link
								to={{ pathname: '/about-registration', state: { data } }}
							>

								Why I need Registration?
							</Link>
							*/}
						</>
					}
				</div>
			</div> : null}
		</div>
	);
};

export default InquiryForm;
